import React, { useState, useCallback } from 'react';
import * as XLSX from 'xlsx';
import { ProductDraft } from './ProductDialog';

interface HardwareBulkImportDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (products: ProductDraft[], domain: 'computers' | 'unified-collaboration') => Promise<void>;
}

interface ParsedProduct {
  row: number;
  data: ProductDraft;
  errors: string[];
  warnings: string[];
}

type HardwareDomain = 'computers' | 'unified-collaboration';

const VALID_SUBCATEGORIES: Record<HardwareDomain, string[]> = {
  'computers': ['Laptop', 'Desktop'],
  'unified-collaboration': ['Headsets', 'Conferencing']
};

const DOMAIN_LABELS: Record<HardwareDomain, string> = {
  'computers': 'Computers',
  'unified-collaboration': 'Unified Collaboration'
};

export function HardwareBulkImportDialog({ open, onClose, onConfirm }: HardwareBulkImportDialogProps) {
  const [selectedDomain, setSelectedDomain] = useState<HardwareDomain | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [parsedProducts, setParsedProducts] = useState<ParsedProduct[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [parseError, setParseError] = useState<string | null>(null);

  const handleDomainSelect = useCallback((domain: HardwareDomain) => {
    setSelectedDomain(domain);
    // Reset file and parsed products when domain changes
    setFile(null);
    setParsedProducts([]);
    setParseError(null);
  }, []);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setParseError(null);
      setParsedProducts([]);
    }
  }, []);

  const parseFile = useCallback(async () => {
    if (!file || !selectedDomain) return;
    
    setIsProcessing(true);
    setParseError(null);
    
    try {
      let rows: any[] = [];
      const isExcel = file.name.endsWith('.xlsx') || file.name.endsWith('.xls');

      if (isExcel) {
        // Parse Excel file
        const data = await file.arrayBuffer();
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        rows = XLSX.utils.sheet_to_json(firstSheet, { header: 1 });
      } else {
        // Parse CSV file
        const text = await file.text();
        const lines = text.split('\n').filter(line => line.trim());
        rows = lines.map(line => parseCSVLine(line));
      }

      if (rows.length < 2) {
        setParseError('File must contain a header row and at least one data row');
        setIsProcessing(false);
        return;
      }

      // Parse header - normalize to lowercase for matching
      const headers = rows[0].map((h: any) => String(h || '').trim().toLowerCase());
      
      // Parse rows
      const products: ParsedProduct[] = [];
      
      for (let i = 1; i < rows.length; i++) {
        const values = rows[i];
        if (!values || values.length === 0 || !values.some((v: any) => v)) continue; // Skip empty rows
        
        const errors: string[] = [];
        const warnings: string[] = [];
        
        // Map columns to product fields
        const getField = (...fieldNames: string[]) => {
          for (const field of fieldNames) {
            const idx = headers.indexOf(field.toLowerCase());
            if (idx >= 0 && values[idx] !== undefined && values[idx] !== null) {
              return String(values[idx]).trim();
            }
          }
          return '';
        };

        // Extract fields based on CSV columns
        const domain = getField('domain').toLowerCase();
        const subCategory = getField('sub-category', 'subcategory', 'sub category');
        const name = getField('product name', 'productname', 'name', 'product');
        const brand = getField('brand', 'vendor', 'manufacturer');
        const priceStr = getField('price', 'cost');
        // Pricing Type is ignored - always lumpSum for these domains
        const feature1 = getField('feature 1', 'feature1');
        const feature2 = getField('feature 2', 'feature2');
        const feature3 = getField('feature 3', 'feature3');
        const feature4 = getField('feature 4', 'feature4');
        const feature5 = getField('feature 5', 'feature5');
        const displayOrderStr = getField('display order', 'displayorder', 'order');
        const sku = getField('sku', 'product sku', 'part number');
        const sohStr = getField('soh', 'stock', 'stock on hand', 'quantity');

        // Validation - Required fields
        if (!name) errors.push('Product Name is required');
        if (!brand) errors.push('Brand is required');

        // Domain validation - must match selected domain
        if (!domain) {
          errors.push('Domain is required');
        } else if (domain !== selectedDomain) {
          errors.push(`Domain "${domain}" does not match selected domain "${selectedDomain}"`);
        }

        // Sub-category validation (warning only)
        if (subCategory && !VALID_SUBCATEGORIES[selectedDomain].map(s => s.toLowerCase()).includes(subCategory.toLowerCase())) {
          warnings.push(`Sub-category "${subCategory}" is not standard for ${DOMAIN_LABELS[selectedDomain]}. Valid options: ${VALID_SUBCATEGORIES[selectedDomain].join(', ')}`);
        }

        // Parse price
        const price = parseFloat(priceStr) || 0;
        if (price < 0) errors.push('Price must be >= 0');

        // Parse display order
        const displayOrder = displayOrderStr ? parseInt(displayOrderStr) : undefined;
        if (displayOrderStr && isNaN(parseInt(displayOrderStr))) {
          warnings.push('Display Order should be a number');
        }

        // Parse SOH (Stock on Hand)
        const soh = sohStr ? parseInt(sohStr) : undefined;
        if (sohStr && isNaN(parseInt(sohStr))) {
          warnings.push('SOH should be a number');
        }

        // Combine features into array, filtering empty values
        const features = [feature1, feature2, feature3, feature4, feature5]
          .filter(f => f && f.trim())
          .map(f => f.trim());

        // Normalize sub-category to proper case if it matches a valid option
        let normalizedSubCategory: string | undefined = undefined;
        if (subCategory) {
          const matchedSubCat = VALID_SUBCATEGORIES[selectedDomain].find(
            s => s.toLowerCase() === subCategory.toLowerCase()
          );
          normalizedSubCategory = matchedSubCat || subCategory;
        }

        const product: ProductDraft = {
          kind: 'hardware',
          domain: selectedDomain,
          name,
          vendorOrBrand: brand,
          price,
          pricingType: 'lumpSum',
          features: features.length > 0 ? features : undefined,
          subCategory: normalizedSubCategory,
          displayOrder,
          sku: sku || undefined,
          soh,
        };

        products.push({
          row: i + 1,
          data: product,
          errors,
          warnings
        });
      }

      setParsedProducts(products);
    } catch (e: any) {
      setParseError(e.message || 'Failed to parse file');
    } finally {
      setIsProcessing(false);
    }
  }, [file, selectedDomain]);

  const handleCancel = useCallback(() => {
    setSelectedDomain(null);
    setFile(null);
    setParsedProducts([]);
    setParseError(null);
    onClose();
  }, [onClose]);

  const handleConfirm = useCallback(async () => {
    if (!selectedDomain) return;
    
    const validProducts = parsedProducts.filter(p => p.errors.length === 0).map(p => p.data);
    if (validProducts.length === 0) return;
    
    setIsProcessing(true);
    try {
      await onConfirm(validProducts, selectedDomain);
      handleCancel();
    } catch (e) {
      setParseError('Failed to import products. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  }, [parsedProducts, selectedDomain, onConfirm, handleCancel]);

  const downloadTemplate = useCallback(() => {
    if (!selectedDomain) return;

    // Generate template CSV client-side
    const headers = [
      'Domain',
      'Sub-category',
      'Product Name',
      'Brand',
      'Price',
      'Pricing Type',
      'SKU',
      'SOH',
      'Feature 1',
      'Feature 2',
      'Feature 3',
      'Feature 4',
      'Feature 5',
      'Display Order'
    ];

    let exampleRows: string[][] = [];
    
    if (selectedDomain === 'computers') {
      exampleRows = [
        ['computers', 'Laptop', 'Dell Latitude 5540', 'Dell', '1899.00', 'Lump Sum', 'DELL-LAT5540', '15', 'Intel Core i7-1365U', '16GB DDR5 RAM', '512GB NVMe SSD', '15.6" FHD Display', 'Windows 11 Pro', '1'],
        ['computers', 'Desktop', 'HP EliteDesk 800 G9', 'HP', '1499.00', 'Lump Sum', 'HP-ED800G9', '8', 'Intel Core i5-13500', '8GB DDR5 RAM', '256GB SSD', 'Windows 11 Pro', '', '2']
      ];
    } else {
      exampleRows = [
        ['unified-collaboration', 'Headsets', 'Jabra Evolve2 75', 'Jabra', '549.00', 'Lump Sum', 'JAB-EV275', '25', 'Active Noise Cancellation', '40mm speakers', 'Bluetooth 5.2', 'Microsoft Teams certified', '', '1'],
        ['unified-collaboration', 'Conferencing', 'Poly Studio P15', 'Poly', '999.00', 'Lump Sum', 'POLY-P15', '5', '4K Camera', 'NoiseBlockAI', 'Autoframing', 'USB-C connectivity', '', '2']
      ];
    }

    const csvContent = [
      headers.join(','),
      ...exampleRows.map(row => row.map(cell => 
        // Escape cells that contain commas or quotes
        cell.includes(',') || cell.includes('"') ? `"${cell.replace(/"/g, '""')}"` : cell
      ).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bulk_upload_${selectedDomain === 'computers' ? 'computers' : 'uc'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [selectedDomain]);

  const validCount = parsedProducts.filter(p => p.errors.length === 0).length;
  const errorCount = parsedProducts.filter(p => p.errors.length > 0).length;
  const warningCount = parsedProducts.filter(p => p.warnings.length > 0 && p.errors.length === 0).length;

  if (!open) return null;

  return (
    <div role="dialog" aria-modal="true" className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/30" onClick={handleCancel} />
      <div className="relative z-10 w-full max-w-5xl rounded-2xl bg-white shadow-xl border border-gray-200 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Bulk Import - Computers & Unified Collaboration</h2>
            <p className="text-sm text-gray-500 mt-1">Import hardware products from CSV or Excel file</p>
          </div>
          <button onClick={handleCancel} className="text-gray-500 hover:text-gray-700" aria-label="Close dialog">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto p-6">
          {/* Step 1: Domain Selection */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Step 1: Select Domain</h3>
            <div className="flex gap-3">
              <button
                onClick={() => handleDomainSelect('computers')}
                className={`flex-1 p-4 rounded-lg border-2 transition-all ${
                  selectedDomain === 'computers'
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    selectedDomain === 'computers' ? 'bg-blue-100' : 'bg-gray-100'
                  }`}>
                    <svg className={`w-5 h-5 ${selectedDomain === 'computers' ? 'text-blue-600' : 'text-gray-500'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div className="text-left">
                    <div className={`font-medium ${selectedDomain === 'computers' ? 'text-blue-900' : 'text-gray-900'}`}>Computers</div>
                    <div className="text-xs text-gray-500">Laptops, Desktops</div>
                  </div>
                </div>
              </button>

              <button
                onClick={() => handleDomainSelect('unified-collaboration')}
                className={`flex-1 p-4 rounded-lg border-2 transition-all ${
                  selectedDomain === 'unified-collaboration'
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    selectedDomain === 'unified-collaboration' ? 'bg-blue-100' : 'bg-gray-100'
                  }`}>
                    <svg className={`w-5 h-5 ${selectedDomain === 'unified-collaboration' ? 'text-blue-600' : 'text-gray-500'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div className="text-left">
                    <div className={`font-medium ${selectedDomain === 'unified-collaboration' ? 'text-blue-900' : 'text-gray-900'}`}>Unified Collaboration</div>
                    <div className="text-xs text-gray-500">Headsets, Conferencing</div>
                  </div>
                </div>
              </button>
            </div>
          </div>

          {/* Step 2: Upload (only shown after domain selection) */}
          {selectedDomain && (
            <>
              {/* Instructions */}
              <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h3 className="text-sm font-semibold text-blue-900 mb-2">Step 2: Upload CSV File</h3>
                <div className="text-sm text-blue-800 space-y-1">
                  <p>Upload a CSV file with your {DOMAIN_LABELS[selectedDomain]} products.</p>
                </div>
                
                <div className="mt-3 pt-3 border-t border-blue-200">
                  <p className="text-xs font-semibold text-blue-900 mb-1.5">Required Columns:</p>
                  <ul className="text-xs text-blue-800 space-y-0.5 ml-4">
                    <li>• <strong>Domain</strong> - Must be "{selectedDomain}"</li>
                    <li>• <strong>Product Name</strong> - Product name</li>
                    <li>• <strong>Brand</strong> - Manufacturer/Brand</li>
                  </ul>
                  
                  <p className="text-xs font-semibold text-blue-900 mb-1.5 mt-2">Optional Columns:</p>
                  <ul className="text-xs text-blue-800 space-y-0.5 ml-4">
                    <li>• <strong>Sub-category</strong> - {VALID_SUBCATEGORIES[selectedDomain].join(' or ')}</li>
                    <li>• <strong>Price</strong> - Product price (defaults to 0)</li>
                    <li>• <strong>SKU</strong> - Product SKU/part number</li>
                    <li>• <strong>SOH</strong> - Stock on Hand (quantity available)</li>
                    <li>• <strong>Feature 1-5</strong> - Product features (displayed as bullet points)</li>
                    <li>• <strong>Display Order</strong> - Number for sorting (lower = first)</li>
                  </ul>
                  
                  <p className="text-xs text-blue-700 mt-2 italic">Note: Pricing Type column is ignored - these products are always Lump Sum.</p>
                </div>
              </div>

              {/* Upload Section */}
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-3">
                  <label className="block">
                    <span className="sr-only">Choose file</span>
                    <input
                      type="file"
                      accept=".csv,.xlsx,.xls"
                      onChange={handleFileChange}
                      className="block text-sm text-gray-900 border border-gray-300 rounded-lg cursor-pointer bg-gray-50 focus:outline-none"
                    />
                  </label>
                  <button
                    onClick={downloadTemplate}
                    className="inline-flex items-center px-3 py-2 text-sm border border-gray-300 rounded-md bg-white hover:bg-gray-50"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path d="M12 4v16m0 0l-4-4m4 4l4-4" />
                    </svg>
                    Download Template
                  </button>
                </div>
                
                {file && !parsedProducts.length && (
                  <button
                    onClick={parseFile}
                    disabled={isProcessing}
                    className="inline-flex items-center px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300"
                  >
                    {isProcessing ? 'Parsing...' : 'Parse File'}
                  </button>
                )}

                {parseError && (
                  <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-800">
                    {parseError}
                  </div>
                )}
              </div>

              {/* Preview Section */}
              {parsedProducts.length > 0 && (
                <div>
                  <div className="mb-3 flex items-center justify-between">
                    <h3 className="text-sm font-medium text-gray-900">
                      Preview: {validCount} valid, {errorCount} with errors{warningCount > 0 && `, ${warningCount} with warnings`}
                    </h3>
                  </div>

                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <div className="max-h-96 overflow-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50 sticky top-0">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Row</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Product Name</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Brand</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Sub-category</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Price</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">SKU</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">SOH</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Features</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Order</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Status</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {parsedProducts.map((p, idx) => (
                            <tr key={idx} className={p.errors.length > 0 ? 'bg-red-50' : p.warnings.length > 0 ? 'bg-amber-50' : ''}>
                              <td className="px-3 py-2 text-sm text-gray-900">{p.row}</td>
                              <td className="px-3 py-2 text-sm text-gray-900 max-w-[200px] truncate" title={p.data.name}>{p.data.name || '-'}</td>
                              <td className="px-3 py-2 text-sm text-gray-600">{p.data.vendorOrBrand || '-'}</td>
                              <td className="px-3 py-2 text-sm text-gray-600">{p.data.subCategory || '-'}</td>
                              <td className="px-3 py-2 text-sm text-gray-600">${(p.data.price ?? 0).toFixed(2)}</td>
                              <td className="px-3 py-2 text-sm text-gray-600 font-mono text-xs">{p.data.sku || '-'}</td>
                              <td className="px-3 py-2 text-sm text-gray-600">{(p.data as any).soh ?? '-'}</td>
                              <td className="px-3 py-2 text-sm text-gray-600">
                                {p.data.features && p.data.features.length > 0 ? (
                                  <span title={p.data.features.join(', ')}>{p.data.features.length} feature(s)</span>
                                ) : '-'}
                              </td>
                              <td className="px-3 py-2 text-sm text-gray-600">{p.data.displayOrder ?? '-'}</td>
                              <td className="px-3 py-2 text-sm">
                                {p.errors.length > 0 ? (
                                  <div className="text-red-600 text-xs">
                                    {p.errors.map((err, i) => (
                                      <div key={i}>• {err}</div>
                                    ))}
                                  </div>
                                ) : p.warnings.length > 0 ? (
                                  <div>
                                    <span className="text-green-600 text-xs">Valid</span>
                                    <div className="text-amber-600 text-xs mt-1">
                                      {p.warnings.map((warn, i) => (
                                        <div key={i}>• {warn}</div>
                                      ))}
                                    </div>
                                  </div>
                                ) : (
                                  <span className="text-green-600 text-xs">Valid</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between bg-gray-50">
          <div className="text-sm text-gray-600">
            {validCount > 0 && (
              <span>{validCount} product{validCount !== 1 ? 's' : ''} ready to import to {selectedDomain && DOMAIN_LABELS[selectedDomain]}</span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleCancel}
              className="px-4 py-2 text-sm border border-gray-300 rounded-md bg-white hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={validCount === 0 || isProcessing || !selectedDomain}
              className={`px-4 py-2 text-sm rounded-md ${
                validCount > 0 && !isProcessing && selectedDomain
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              {isProcessing ? 'Importing...' : `Import ${validCount} Product${validCount !== 1 ? 's' : ''}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Helper function to parse CSV line (handles quoted fields with commas)
function parseCSVLine(line: string): string[] {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;
  
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    
    if (char === '"') {
      inQuotes = !inQuotes;
    } else if (char === ',' && !inQuotes) {
      result.push(current);
      current = '';
    } else {
      current += char;
    }
  }
  
  result.push(current);
  return result;
}

export default HardwareBulkImportDialog;
